import { Icon, useTheme } from "@outschool/backpack";
import { faChevronLeft, faChevronRight } from "@outschool/icons";
import { useDimensions, useOnScreen } from "@outschool/ui-utils";
import { SystemStyleObject } from "@styled-system/css";
import React from "react";

import { LegacyBox } from "./LegacyBox";
import LegacyButton from "./LegacyButton/LegacyButton";
import LegacyFlex from "./LegacyFlex";

const SCROLL_ARROW_VARIANT_STYLES = {
  left: {
    left: "-1px",
    maskImage: "linear-gradient(to left, transparent 0%, black 30%);",
  },
  right: {
    right: "-1px",
    maskImage: "linear-gradient(to right, transparent 0%, black 30%);",
  },
};

function ScrollArrow({
  variant,
  onClick = () => {},
  sx = {},
  arrowClickOnBox = false,
  useSmallArrow = false,
  arrowButtonSx = {},
}: {
  variant: "left" | "right";
  onClick: (e: React.SyntheticEvent) => void;
  sx?: SystemStyleObject;
  arrowClickOnBox?: boolean;
  useSmallArrow?: boolean;
  arrowButtonSx?: SystemStyleObject;
}) {
  return (
    <LegacyFlex
      sx={{
        position: "absolute",
        top: 0,
        zIndex: 1,
        width: useSmallArrow ? 50 : 68,
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        opacity: 1,
        transition: "opacity 0.1s ease-in-out",
        ...SCROLL_ARROW_VARIANT_STYLES[variant],
        ...sx,
        ...(arrowClickOnBox && {
          cursor: "pointer",
        }),
      }}
      role={arrowClickOnBox ? "button" : undefined}
    >
      <LegacyButton
        color="secondary"
        round
        onClick={onClick}
        size={useSmallArrow ? "small" : "medium"}
        style={{
          ...arrowButtonSx,
        }}
      >
        <Icon icon={variant === "left" ? faChevronLeft : faChevronRight} />
      </LegacyButton>
    </LegacyFlex>
  );
}

export type CarouselProps = {
  children: JSX.Element[] | JSX.Element;
  innerSx?: SystemStyleObject;
  sx?: SystemStyleObject;
  arrowSx?: SystemStyleObject;
  arrowClickOnBox?: boolean;
  scrollOnMobile?: boolean;
  leftAlign?: boolean;
  useSmallArrow?: boolean;
  rightArrowSx?: SystemStyleObject;
  leftArrowSx?: SystemStyleObject;
  scrollWidth?: number;
  customScrollFunction?: (
    element: HTMLElement,
    distance: number | null,
    direction: "left" | "right",
  ) => void;
  mobileBreakpoint?: number | string;
  arrowButtonSx?: SystemStyleObject;
  containerWidthOffset?: number;
};

function Carousel(
  {
    children,
    innerSx,
    sx,
    arrowClickOnBox,
    arrowSx,
    scrollOnMobile = false,
    leftAlign = false,
    useSmallArrow = false,
    rightArrowSx,
    leftArrowSx,
    arrowButtonSx,
    scrollWidth,
    customScrollFunction,
    mobileBreakpoint = "md",
    // If the fade at the sides covers the content, use this to offset the width of the container width scroll.
    containerWidthOffset = 0,
  }: CarouselProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const theme = useTheme();

  const [containerNode, setContainerNode] = React.useState<HTMLElement | null>(
    null,
  );
  const [cardContainerNode, setCardContainerNode] =
    React.useState<HTMLElement | null>(null);
  const [leftScrollNode, setLeftScrollNode] =
    React.useState<HTMLElement | null>(null);
  const [rightScrollNode, setRightScrollNode] =
    React.useState<HTMLElement | null>(null);

  const { width: containerWidth } = useDimensions(containerNode);
  const isCompletelyScrolledLeft = useOnScreen(leftScrollNode);
  const isCompletelyScrolledRight = useOnScreen(rightScrollNode);
  arrowSx = {
    // @ts-ignore
    color: sx?.color ?? "gray4",
    // @ts-ignore
    backgroundColor: sx?.backgroundColor ?? "white",
    ...arrowSx,
  };

  const defaultScrollFunction = React.useCallback(
    (
      element: HTMLElement,
      distance: number | null,
      direction: "left" | "right",
    ) => {
      const scrollDistance = scrollWidth ?? distance ?? 250;
      const currentScrollLeft = element.scrollLeft;
      const targetScrollLeft =
        direction === "left"
          ? Math.max(0, currentScrollLeft - scrollDistance)
          : currentScrollLeft + scrollDistance;

      element.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth",
      });
    },
    [scrollWidth],
  );

  const scrollFunction = customScrollFunction ?? defaultScrollFunction;

  return (
    <LegacyBox ref={ref} sx={sx}>
      <LegacyBox ref={setContainerNode} sx={{ position: "relative" }}>
        <ScrollArrow
          variant="left"
          onClick={(e: React.SyntheticEvent) => {
            e.preventDefault();
            scrollFunction(
              cardContainerNode!,
              containerWidth ? containerWidth - containerWidthOffset : null,
              "left",
            );
          }}
          sx={{
            [theme.breakpoints.down(mobileBreakpoint as any)]: {
              display: scrollOnMobile ? "" : "none",
            },
            opacity: isCompletelyScrolledLeft ? 0 : 1,
            pointerEvents: isCompletelyScrolledLeft ? "none" : "auto",
            ...arrowSx,
            ...leftArrowSx,
          }}
          arrowClickOnBox={arrowClickOnBox}
          useSmallArrow={useSmallArrow}
          arrowButtonSx={{
            ...arrowButtonSx,
          }}
        />
        <LegacyFlex
          ref={setCardContainerNode}
          sx={{
            justifyContent: "flex-start",
            overflowX: "auto",
            scrollbarWidth: "none",
            "::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <LegacyBox
            ref={setLeftScrollNode}
            sx={{ width: "1px", flexShrink: 0 }}
          />
          <LegacyFlex
            sx={{
              flexGrow: 1,
              flexShrink: 0,
              flexBasis: 0,
              justifyContent: leftAlign ? "start" : "center",
              ...(typeof innerSx === "function" ? innerSx(theme) : innerSx),
            }}
          >
            {children}
          </LegacyFlex>
          <LegacyBox
            ref={setRightScrollNode}
            sx={{ width: "1px", flexShrink: 0 }}
          />
        </LegacyFlex>
        <ScrollArrow
          variant="right"
          onClick={(e: React.SyntheticEvent) => {
            e.preventDefault();
            scrollFunction(
              cardContainerNode!,
              containerWidth ? containerWidth - containerWidthOffset : null,
              "right",
            );
          }}
          arrowClickOnBox={arrowClickOnBox}
          sx={{
            [theme.breakpoints.down(mobileBreakpoint as any)]: {
              display: scrollOnMobile ? "" : "none",
            },
            opacity: isCompletelyScrolledRight ? 0 : 1,
            pointerEvents: isCompletelyScrolledRight ? "none" : "auto",
            ...arrowSx,
            ...rightArrowSx,
          }}
          useSmallArrow={useSmallArrow}
          arrowButtonSx={{
            ...arrowButtonSx,
          }}
        />
      </LegacyBox>
    </LegacyBox>
  );
}

export default React.forwardRef(Carousel);
